import { useEffect, useRef, useState } from "react";
import API from "../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { PaperPlaneTilt, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const TITLES = { chase: "Chase payment", winback: "Win back trial" };

/** Pre-written Fixlr-branded email to a workspace owner; editable, previewed server-side, sent via Fixlr. */
export default function RecoveryOutreachDialog({ open, kind, tenantId, onClose, onSent }) {
  const [draft, setDraft] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [extend, setExtend] = useState(false);
  const [extendDays, setExtendDays] = useState(7);
  const [offer, setOffer] = useState(false);
  const [offerPct, setOfferPct] = useState(20);
  const [html, setHtml] = useState("");
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const days = extend ? extendDays : 0;
  const pct = kind === "winback" && offer ? offerPct : 0;

  useEffect(() => {
    if (!open || !tenantId) return;
    setDraft(null); setHtml(""); setExtend(false); setOffer(false);
    API.get(`/platform/payment-recovery/outreach/${tenantId}/draft`, { params: { kind } })
      .then(({ data }) => { setDraft(data); setSubject(data.subject); setMessage(data.message); setExtendDays(data.extend_days_default || 7); setOfferPct(data.offer_pct_default || 20); setOffer(kind === "winback" && !!data.offer_on_default && !data.row?.offer?.active); })
      .catch((e) => { toast.error(e.response?.data?.detail || "Couldn't prepare the email"); onClose(); });
  }, [open, kind, tenantId, onClose]);

  useEffect(() => {
    if (!draft) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      API.post(`/platform/payment-recovery/outreach/${tenantId}`, { kind, subject, message, extend_trial_days: days, offer_pct: pct, preview: true })
        .then(({ data }) => setHtml(data.html)).catch(() => {});
    }, 400);
    return () => clearTimeout(timer.current);
  }, [draft, subject, message, days, pct, kind, tenantId]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(`/platform/payment-recovery/outreach/${tenantId}`, { kind, subject, message, extend_trial_days: days, offer_pct: pct });
      toast.success(`${TITLES[kind]} email sent to ${data.to}${data.trial_ends_at ? ` · trial extended to ${new Date(data.trial_ends_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}${data.offer?.code ? ` · offer ${data.offer.code}` : ""}`);
      onSent?.(data); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send"); } finally { setBusy(false); }
  };

  const row = draft?.row;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="outreach-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]" data-testid="outreach-title">{TITLES[kind]} — {row?.company_name || row?.slug || "…"}</DialogTitle>
          <DialogDescription data-testid="outreach-to">
            {draft ? <>To <strong>{draft.owner_name || "workspace owner"}</strong> &lt;{draft.to || "no email on file"}&gt; · sent from Fixlr, Fixlr-branded{draft.reply_to ? <> · replies land in the <strong>Owner replies</strong> inbox ({draft.reply_to})</> : ""}{row?.chased_at ? ` · last chased ${new Date(row.chased_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} (${row.chase_count}×)` : ""}{row?.winback_at ? ` · last win-back ${new Date(row.winback_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} (${row.winback_count}×)` : ""}</> : "Preparing…"}
          </DialogDescription>
        </DialogHeader>
        {draft && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Subject</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="outreach-subject" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Message</Label>
                  <button type="button" className="text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" onClick={() => { setSubject(draft.subject); setMessage(draft.message); }} data-testid="outreach-reset"><ArrowCounterClockwise size={10} /> Reset to template</button>
                </div>
                <Textarea rows={13} value={message} onChange={(e) => setMessage(e.target.value)} className="text-sm" data-testid="outreach-message" />
                <p className="text-[10px] text-muted-foreground mt-1">{kind === "chase" ? "The outstanding amount, read-only countdown and “Update card” button are added automatically." : "The “Log in” button is added automatically."}</p>
              </div>
              {kind === "winback" && (
                <div className="flex items-center gap-2 rounded-md border border-border p-2.5 text-xs whitespace-nowrap flex-wrap" data-testid="outreach-extend-row">
                  <Checkbox id="extend-trial" checked={extend} onCheckedChange={(v) => setExtend(!!v)} data-testid="outreach-extend-trial" />
                  <label htmlFor="extend-trial" className="cursor-pointer">Also extend the trial by</label>
                  <Input type="number" min={1} max={90} value={extendDays} disabled={!extend} onChange={(e) => setExtendDays(Math.max(1, Math.min(90, parseInt(e.target.value, 10) || 1)))} className="h-7 w-16 text-xs" data-testid="outreach-extend-days" />
                  <span>days so they can get back in</span>
                </div>
              )}
              {kind === "winback" && (
                <div className="flex items-center gap-2 rounded-md border border-border p-2.5 text-xs whitespace-nowrap flex-wrap" data-testid="outreach-offer-row">
                  <Checkbox id="offer-code" checked={offer} onCheckedChange={(v) => setOffer(!!v)} disabled={!!row?.offer?.active} data-testid="outreach-offer" />
                  <label htmlFor="offer-code" className="cursor-pointer">Include a first-month discount of</label>
                  <Input type="number" min={5} max={90} step={5} value={offerPct} disabled={!offer || !!row?.offer?.active} onChange={(e) => setOfferPct(Math.max(5, Math.min(90, parseInt(e.target.value, 10) || 5)))} className="h-7 w-16 text-xs" data-testid="outreach-offer-pct" />
                  <span>% (single-use code, valid {draft.offer_valid_days || 30} days, applied automatically at checkout)</span>
                  {row?.offer?.active && <span className="text-emerald-700 basis-full" data-testid="outreach-offer-existing">Offer {row.offer.code} ({row.offer.pct}% off) is already live until {new Date(row.offer.expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} — it will be mentioned again.</span>}
                </div>
              )}
            </div>
            <div className="border border-border rounded-md overflow-hidden bg-muted/30 min-h-[420px]" data-testid="outreach-preview">
              <p className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground border-b border-border bg-muted/40">EMAIL PREVIEW</p>
              {html ? <iframe title="Email preview" srcDoc={html} sandbox="" className="w-full h-[440px] bg-white" data-testid="outreach-preview-frame" /> : <p className="p-4 text-xs text-muted-foreground">Rendering…</p>}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="outreach-cancel-btn">Cancel</Button>
          <Button onClick={send} disabled={busy || !draft || !draft.to} data-testid="outreach-send-btn"><PaperPlaneTilt size={14} className="mr-1" /> {busy ? "Sending…" : [days ? `extend ${days}d` : null, pct ? `${pct}% offer` : null].filter(Boolean).length ? `Send with ${[days ? `${days}d extension` : null, pct ? `${pct}% offer` : null].filter(Boolean).join(" + ")}` : "Send email"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
